import getLocal from '@/app/utils/getLocal';
import { FormatMoneyLocal, FormatNumber } from '@elem/ui-common/utils/FormatMoneyLocal';
import classNames from 'classnames';
import EFPoint from 'elem/ui-assets/icons/unregular/EFPoint';
import React from 'react';
import { Autoplay, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import EfImg from '../ef-img';
import EfLink from '../ef-link';
import EfVideo from '../ef-video';
import './index.scss';
import { langText } from './langText';
import { useGlobal } from '@/utils/useGlobal';
import { saveAICookieForShopify } from '@elem/ui-common/utils/CookieForShopify';

import { useState } from 'react';

export interface EfSwiperItemProps {
    /**
     * 图片/视频资源路径
     */
    img?: string;
    /**
     * 图片/视频资源路径
     */
    pc?: string;
    /**
     * 图片/视频资源路径
     */
    h5?: string;
    /**
     * isVideo
     */
    isVideo?: boolean;
    /**
     * poster
     */
    poster?: string;
    /**
     * posterH5
     */
    posterH5?: string;
    /**
     * imgs: rapid jp bundles页面使用
     */
    imgs?: {
        img: string;
        text: string;
        color: string;
        productId: string;
        variantId: string;
        title: string;
        showBtns?: number[];
        linkMore?: string;
    }[];
}

export interface EFSwiperCardInfoProps {
    /**
     * tag
     */
    tag?: string | undefined;
    /**
     * title
     */
    title: string;
    /**
     * subtitle
     */
    subtitle?: string;
    /**
     * productId
     */
    productId?: string;
    /**
     * quantity 购买数量
     */
    quantity?: number;
    /**
     * variantId
     */
    variantId?: string;
    /**
     * discountPrice
     */
    discountPrice: number;
    /**
     * inventory 库存
     */
    inventory?: number;
    /**
     * 库存策略
     */
    inventoryPolicy?: 'deny' | 'continue';
    /**
     * 原价
     */
    originalPrice?: number;
    /**
     * img
     */
    img?: string;
    /**
     * pointRate 积分倍数
     */
    pointRate?: number;
    /**
     * point 积分
     */
    point?: string | number;
    /**
     * 是否显示Coming Soon按钮 Coming Soon
     */
    showBtnComing?: boolean;
    /**
     * 是否显示【添加购物车】按钮 Add to Cart
     */
    showBtnCart?: boolean;
    /**
     * 是否显示【立即购买】按钮 Buy Now
     */
    showBtnBuy?: boolean;
    /**
     * 是否显示【了解更多】按钮 Learn More
     */
    showBtnMore?: boolean;
    /**
     * 按钮 Learn More 的链接
     */
    linkMore?: string;
    /**
     * 按钮 Learn More 的文案
     */
    linkMoreText?: React.ReactNode;
    /**
     * 描述
     */
    desc?: string;
    /**
     * tips: 补充小文案，如延迟发货等
     */
    tips?: string[];
    /**
     * 自定义 buy url
     */
    buyUrl?: string;
    /**
     * hideTag: 是否隐藏tag
     */
    hideTag?: boolean;
    /**
     * Buy Now 按钮文案
     */
    link_buy?: boolean;
    /**
     * Add to Cart 按钮文案
     */
    link_cart?: boolean;
}

export interface EfSwiperCardBundleProps {
    /**
     * 样式名
     */
    className?: string;
    /**
     * swiper autoPlay 是否自动轮播
     */
    autoPlay?: boolean;
    /**
     * swiper列表
     */
    items: EfSwiperItemProps[];
    /**
     * 商品信息
     */
    info: EFSwiperCardInfoProps;
    /**
     * direction 方向（ltr rtl) 默认ltr： 左侧轮播，右侧信息
     */
    direction?: 'ltr' | 'rtl';
    /**
     * 添加购物车回调
     */
    callbackCart?: any;
    /**
     * gtm_position
     */
    gtm_position?: string;
    /**
     * gtm list_name
     */
    list_name?: string;
    /*
     * more
     */
    gtm_more_dataLayer?: any;
    /**
     * active 当前激活的索引
     */
    active?: number;
}

/**
 * 维护: kalkin
 *
 * bundle卡片（JP站常见，DELTA 3 \ DELTA Pro 3 \ RIVER 3 \ Rapid）: 一侧swiper轮播 + 一侧商品信息
 */
const EfSwiperCardBundle: React.FC<EfSwiperCardBundleProps> = ({
    items = [],
    className,
    autoPlay = true,
    info,
    direction = 'ltr',
    list_name = '',
    callbackCart,
    gtm_position,
    gtm_more_dataLayer,
}) => {
    const { gtm } = useGlobal();

    const prefixCls = `ef-swiper-card-bundle`;
    const prefix = `${prefixCls}-`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefix}${direction}`]: direction,
        [`${prefix}single`]: items.length === 1,
    });
    const { locale = 'jp' } = getLocal();
    const i18n = langText[locale] || langText['jp'];
    const [active, setActive] = useState(0);
    const [hover, setHover] = useState(-1);

    const {
        tag = '',
        title,
        subtitle = '',
        desc = '',
        productId,
        variantId,
        quantity = 1,
        discountPrice,
        originalPrice = 0,
        inventory = 0,
        inventoryPolicy,
        img = '',
        pc = '',
        h5 = '',
        pointRate,
        point,
        showBtnComing = false,
        showBtnCart = true,
        showBtnBuy = true,
        showBtnMore = true,
        linkMore = '',
        linkMoreText,
        buyUrl,
        tips = [],
        hideTag = false,
        link_buy = '',
        link_cart = '',
    } = info;

    const handleAddToCart = (info: EFSwiperCardInfoProps, list_name: string) => {
        if (items[0]?.imgs && (items[0]?.imgs[active]?.inventory > 0 || items[0]?.imgs[active]?.inventoryPolicy === 'continue') && callbackCart) {
            const { discountPrice, originalPrice, variantId, inventory, inventoryPolicy, img, title } = items[0]?.imgs[active];
            callbackCart(
                {
                    ...info,
                    discountPrice,
                    originalPrice,
                    variantId,
                    inventory,
                    inventoryPolicy,
                    img,
                    title,
                    buyText: link_cart || i18n.link_cart, //埋点好像需要用这个呀
                },
                list_name,
            );
        }
        if ((Number(info?.inventory || 0) > 0 || info?.inventoryPolicy === 'continue') && callbackCart) {
            callbackCart(
                {
                    ...info,
                    buyText: link_cart || i18n.link_cart, //埋点好像需要用这个呀
                },
                list_name,
            );
        }
    };

    // 更多链接的按钮埋点
    const gtm_more_dataLayer2 = gtm_more_dataLayer || {
        event: 'uaEvent',
        event_name: 'buy',
        product_name: title,
        position: gtm_position || 'Product Lanuch',
    };

    const handleMoreLinkClick = (module: any) => {
        saveAICookieForShopify(linkMore || items[0]?.imgs[active]?.linkMore, locale);

        gtm.push({
            event: 'uaEvent',
            event_name: 'buy',
            product_name: Array.isArray(items[0]?.imgs) ? items[0]?.imgs[active]?.title || title : title,
            position: gtm_position || 'Product Lanuch',
        });
    };

    const handleBuyLinkClick = () => {
        const link = buyUrl
            ? buyUrl
            : `https://${locale}.ecoflow.com/cart/${
                  Array.isArray(items[0]?.imgs) && items[0]?.imgs[active]?.variantId ? items[0]?.imgs[active]?.variantId : variantId
              }:1?channel=buy_button`;
        saveAICookieForShopify(link, locale);

        gtm.push({
            event: 'uaEvent',
            event_name: 'checkout',
            button_name: `${i18n.link_buy}_${title}`,
        });
    };

    return (
        <div className={classes}>
            <div className={`${prefix}auto-swiper`}>
                <Swiper
                    className={`${prefix}swiper-box${items?.length > 1 ? '' : ' single'}`}
                    modules={[Pagination, Autoplay]}
                    spaceBetween={0}
                    loop={items?.length > 1}
                    slidesPerView={'auto'}
                    initialSlide={0}
                    pagination={items?.length > 1 ? { clickable: true } : false}
                    autoplay={
                        items?.length > 1 && autoPlay
                            ? {
                                  delay: 4000,
                                  disableOnInteraction: false,
                                  pauseOnMouseEnter: true,
                              }
                            : false
                    }
                >
                    {items?.map((item: EfSwiperItemProps, k: number) => (
                        <SwiperSlide className={`${prefix}swiper-item${` ${prefix}swiper-item-${k}`}`} key={k}>
                            {item?.isVideo ? (
                                <EfVideo autoPlay loop pc={item?.pc || item?.img} poster={item?.poster} posterH5={item?.posterH5} />
                            ) : item?.imgs && Array.isArray(item?.imgs) ? (
                                <EfImg pc={item?.imgs[active]?.img} />
                            ) : (
                                <EfImg pc={item?.pc || item?.img} h5={item?.h5 || item?.img || item?.pc} />
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            <div className={`${prefix}info-box`}>
                <div className={`${prefix}info-box-inner`}>
                    <div className={`${prefix}info-top`}>
                        {(originalPrice !== discountPrice || pointRate || tag) && (
                            <div className={`${prefix}activity`}>
                                {originalPrice !== discountPrice && !hideTag && (
                                    <span className={`${prefix}off`}>{`${Math.round(((originalPrice - discountPrice) / originalPrice) * 100)}% ${
                                        i18n.off
                                    }`}</span>
                                )}
                                {pointRate && (
                                    <div className={`${prefix}point`}>
                                        <EFPoint />
                                        <span className={`${prefix}point-rate`}>
                                            {pointRate}
                                            {i18n.times}
                                        </span>
                                        <span className={`${prefix}point-text`}>
                                            {FormatNumber(point || Math.round((discountPrice / 150) * pointRate))}
                                            {i18n.point}
                                        </span>
                                    </div>
                                )}
                                {tag && <p className={`${prefix}info-tag for-single`}>{tag}</p>}
                            </div>
                        )}
                        {tag && <p className={`${prefix}info-tag for-multi`}>{tag}</p>}
                        {((Array.isArray(items[0]?.imgs) && items[0]?.imgs[active]?.title) || title) && (
                            <p className={`${prefix}info-title`}>{Array.isArray(items[0]?.imgs) ? items[0]?.imgs[active]?.title || title : title}</p>
                        )}
                        {subtitle && <p className={`${prefix}info-subtitle`}>{subtitle}</p>}
                        {desc && <p className={`${prefix}info-desc`}>{desc}</p>}
                        {items?.length > 0 && Array.isArray(items[0]?.imgs) && (
                            <ul className={`${prefix}info-colors`}>
                                {items[0]?.imgs?.map((ig, k) => (
                                    <li className={`${prefix}info-color ${active === k ? 'active' : ''}`} key={k}>
                                        {hover === k && <span className={`${prefix}info-color-text`}>{ig.text}</span>}
                                        <span
                                            className={`${prefix}info-color-outer`}
                                            style={{ borderColor: ig.color }}
                                            onMouseEnter={() => setHover(k)}
                                            onMouseLeave={() => setHover(-1)}
                                            onClick={() => setActive(k)}
                                        >
                                            <span className={`${prefix}info-color-inner`} style={{ backgroundColor: ig?.color }}></span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className={`${prefix}info-bot`}>
                        <div className={`${prefix}info-price`}>
                            <span className={`${prefix}price-discount`}>
                                {FormatMoneyLocal(
                                    items[0]?.imgs && Array.isArray(items[0]?.imgs) ? items[0].imgs[active]?.discountPrice : discountPrice,
                                    locale,
                                )}
                                {i18n.price_suffix}
                            </span>
                            {((discountPrice && discountPrice !== originalPrice) ||
                                (items[0]?.imgs &&
                                    Array.isArray(items[0]?.imgs) &&
                                    items[0].imgs[active]?.originalPrice !== items[0].imgs[active]?.discountPrice)) && (
                                <span className={`${prefix}price-original`}>
                                    {i18n.price_prefix}
                                    {FormatMoneyLocal(
                                        items[0]?.imgs && Array.isArray(items[0]?.imgs) ? items[0].imgs[active]?.originalPrice : originalPrice,
                                        locale,
                                    )}
                                    {i18n.price_suffix}
                                </span>
                            )}
                        </div>
                        <div
                            className={`${prefix}info-btns ${prefix}info-btns-${[showBtnCart, showBtnBuy, showBtnMore]?.filter((boo: boolean) => boo)?.length}`}
                        >
                            {/* Coming Soon 按钮 */}
                            {(showBtnComing || (items?.length > 0 && Array.isArray(items[0]?.imgs) && items[0]?.imgs[active]?.showBtns?.includes(1))) && (
                                <button className={`${prefix}btn disable`}>{i18n.link_coming}</button>
                            )}
                            {/* Learn More 按钮 */}
                            {((items?.length > 0 &&
                                Array.isArray(items[0]?.imgs) &&
                                items[0]?.imgs[active]?.showBtns?.includes(2) &&
                                items[0]?.imgs[active]?.linkMore) ||
                                (linkMore && showBtnMore)) && (
                                <EfLink
                                    className={`${prefix}btn ${prefix}btn-more`}
                                    href={linkMore || items[0]?.imgs[active]?.linkMore}
                                    target="_blank"
                                    // dataLayer={{
                                    //     event: 'uaEvent',
                                    //     event_name: 'buy',
                                    //     product_name: Array.isArray(items[0]?.imgs) ? items[0]?.imgs[active]?.title || title : title,
                                    //     position: gtm_position || 'Product Lanuch',
                                    // }}
                                    onClick={() => handleMoreLinkClick(items)}
                                >
                                    {linkMoreText || i18n.link_more}
                                </EfLink>
                            )}
                            {/* Add to Cart 按钮 */}
                            {(showBtnCart || (items?.length > 0 && Array.isArray(items[0]?.imgs) && items[0]?.imgs[active]?.showBtns?.includes(3))) && (
                                <button
                                    className={`${prefix}btn ${prefix}btn-cart${
                                        (inventoryPolicy !== 'continue' && inventory <= 0 && !items[0]?.imgs) ||
                                        (items[0]?.imgs && items[0]?.imgs[active]?.inventory <= 0 && items[0]?.imgs[active]?.inventoryPolicy !== 'continue')
                                            ? ' disable'
                                            : ''
                                    }`}
                                    onClick={() => handleAddToCart(info, list_name)}
                                >
                                    {link_cart || i18n.link_cart}
                                </button>
                            )}
                            {/* Buy Now 按钮 */}
                            {(showBtnBuy || (items?.length > 0 && Array.isArray(items[0]?.imgs) && items[0]?.imgs[active]?.showBtns?.includes(4))) && (
                                <EfLink
                                    className={`${prefix}btn ${prefix}btn-buy${
                                        (inventoryPolicy !== 'continue' && inventory <= 0 && !items[0]?.imgs) ||
                                        (items[0]?.imgs && items[0]?.imgs[active]?.inventory <= 0 && items[0]?.imgs[active]?.inventoryPolicy !== 'continue')
                                            ? ' disable'
                                            : ''
                                    }`}
                                    href={
                                        buyUrl
                                            ? buyUrl
                                            : `https://${locale}.ecoflow.com/cart/${
                                                  Array.isArray(items[0]?.imgs) && items[0]?.imgs[active]?.variantId
                                                      ? items[0]?.imgs[active]?.variantId
                                                      : variantId
                                              }:1?channel=buy_button`
                                    }
                                    target="_blank"
                                    preventDefault={
                                        (inventoryPolicy !== 'continue' && inventory <= 0 && !items[0]?.imgs) ||
                                        (Array.isArray(items[0]?.imgs) &&
                                            items[0]?.imgs &&
                                            items[0]?.imgs[active]?.inventory <= 0 &&
                                            items[0]?.imgs[active]?.inventoryPolicy !== 'continue')
                                    }
                                    // dataLayer={{
                                    //     event: 'uaEvent',
                                    //     event_name: 'checkout',
                                    //     button_name: `${i18n.link_buy}_${title}`,
                                    // }}
                                    onClick={handleBuyLinkClick}
                                >
                                    {link_buy || i18n.link_buy}
                                </EfLink>
                            )}
                        </div>
                        {tips?.length > 0 && (
                            <ul className={`${prefix}info-tips`}>
                                {tips?.map((tip: string, k: number) => (
                                    <li className={`${prefix}info-tip`} key={k}>
                                        {tip}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EfSwiperCardBundle;
